import carePlan from '@/pages/participantDocuments/templates/careplan.json';
import initialAssessment from '@/pages/participantDocuments/templates/initialassessment.json';
import programCompletion from '@/pages/participantDocuments/templates/programcompletion.json';
import type { ProfileValue } from './types';

export type ProfileJsonValue = string | number | boolean | null | ProfileJsonValue[] | ProfileJsonObject;
export interface ProfileJsonObject { [name: string]: ProfileJsonValue }
export interface ProfileDocumentNode {
  key: string; label: string; kind: 'heading' | 'note' | 'group' | 'repeater' | 'text' | 'multiline' | 'date' | 'boolean' | 'choice';
  required: boolean; options: { value: string | number | boolean; label: string }[]; children: ProfileDocumentNode[];
}
export const profileDocumentTypes = ['Care Plan', 'Initial Assessment', 'Program Completion'];
const defaultKeys: Readonly<Record<string, Readonly<Record<string, 'fullName' | 'cin' | 'author' | 'today'>>>> = {
  'Care Plan': { member_name: 'fullName', cin_number: 'cin', leadcaremanager_name: 'author' },
  'Initial Assessment': { '*assessmentDate': 'today', '*membername': 'fullName', '*mediCalID': 'cin' },
  'Program Completion': { member_name: 'fullName', member_cin: 'cin', lead_care_manager_name: 'author', lcm_signature: 'author', completion_date: 'today' },
};
export const profileDocumentDefaultValues = (type: string, values: ProfileValue[], now = new Date()): Record<string, string> => {
  const read = (name: string) => values.find(value => value.name === name)?.value ?? '';
  const data = { fullName: [read('firstName'), read('lastName')].filter(Boolean).join(' ').trim(), cin: read('cin'), author: read('author'), today: now.toISOString().slice(0, 10) };
  return Object.fromEntries(Object.entries(defaultKeys[type] ?? {}).map(([name, key]) => [name, data[key]]));
};
const templates: Readonly<Record<string, unknown>> = { 'Care Plan': carePlan, 'Initial Assessment': initialAssessment, 'Program Completion': programCompletion };
const label = (value: unknown): string => typeof value === 'string' ? value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';
const object = (value: unknown): value is Record<string, unknown> => Boolean(value && typeof value === 'object' && !Array.isArray(value));

/** Templates describe controls only; no stored template can supply executable behavior. */
export const profileDocumentNodes = (template: unknown): ProfileDocumentNode[] => {
  if (!Array.isArray(template)) return [];
  return template.flatMap(value => {
    if (!object(value)) return [];
    const type = String(value.type ?? '').toLowerCase();
    const key = typeof value.key === 'string' ? value.key : typeof value.name === 'string' ? value.name : '';
    const kind = ({ heading: 'heading', header: 'heading', section: 'group', collapsible: 'group', repeater: 'repeater',
      textinput: 'text', staticinput: 'text', textareainput: 'multiline', dateinput: 'date', yesnoinput: 'boolean',
      listinput: 'choice', listinputdowndown: 'choice' } as Record<string, ProfileDocumentNode['kind']>)[type] ?? 'note';
    const text = label(value.label) || label(value.text);
    if (!key && !['heading', 'note', 'group'].includes(kind)) return [];
    const options: ProfileDocumentNode['options'] = [];
    for (const option of Array.isArray(value.options) ? value.options : []) {
      if (!object(option) || !['string', 'number', 'boolean'].includes(typeof option.value)) continue;
      if (String(option.value) === '') continue;
      if (options.some(item => String(item.value) === String(option.value))) continue;
      options.push({ value: option.value as string | number | boolean, label: label(option.label) || String(option.value) });
    }
    return [{ key, label: text || key, kind, required: value.required === true, options,
      children: profileDocumentNodes(value.children ?? value.fields) }];
  });
};
const compiledTemplates: Readonly<Record<string, ProfileDocumentNode[]>> = Object.fromEntries(Object.entries(templates)
  .map(([name, template]) => [name, profileDocumentNodes(template)]));
export const profileDocumentTemplate = (type: string, retained?: string | null): ProfileDocumentNode[] | undefined => {
  if (retained?.trim()) {
    try {
      const value: unknown = JSON.parse(retained);
      if (Array.isArray(value) && safeJson(value)) {
        const nodes = profileDocumentNodes(value);
        if (nodes.length) return nodes;
      }
    } catch { /* A malformed retained template remains available in its metadata field. */ }
  }
  return Object.prototype.hasOwnProperty.call(compiledTemplates, type) ? compiledTemplates[type] : undefined;
};

const safeJson = (value: unknown, depth = 0): value is ProfileJsonValue => {
  if (depth > 64) return false;
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value) && (!Number.isInteger(value) || Number.isSafeInteger(value));
  if (Array.isArray(value)) return value.every(item => safeJson(item, depth + 1));
  if (object(value)) return Object.values(value).every(item => safeJson(item, depth + 1));
  return false;
};
export const profileDocumentObject = (value: string | null | undefined): ProfileJsonObject | null => {
  if (!value) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return object(parsed) && safeJson(parsed) ? parsed as ProfileJsonObject : null;
  } catch { return null; }
};
export const profileDocumentRequired = (nodes: ProfileDocumentNode[], values: ProfileJsonObject): boolean => nodes.every(node => {
  const value = values[node.key];
  if (node.required && (value == null || value === '' || (Array.isArray(value) && !value.length))) return false;
  if (node.kind === 'group') return profileDocumentRequired(node.children, values);
  if (node.kind === 'repeater' && Array.isArray(value)) return value.every(item => object(item) && profileDocumentRequired(node.children, item as ProfileJsonObject));
  return true;
});
